/**
 * PROMPT 5: Workspace Access Level Mapping
 *
 * Maps internal workspace roles to UI access levels.
 * This is the single source of truth for access level display in the UI.
 *
 * PROMPT 5 Part D Mapping Rules:
 * - workspace_owner or delivery_owner → Owner
 * - workspace_member → Member
 * - stakeholder or workspace_viewer → Guest
 *
 * Note: Guest platform role always maps to Guest workspace access.
 */
import { isWorkspaceMember, isWorkspaceOwner, isWorkspaceViewer } from '@/utils/access';
import { normalizePlatformRole, PLATFORM_ROLE } from '@/utils/roles';
import type { WorkspaceRole } from '@/state/workspace.store';
import { ORG_ROLES, type OrgRole } from '@/types/organization';

export type WorkspaceAccessLevel = 'Owner' | 'Member' | 'Guest';

/**
 * ONE-TRUTH-FE-1: what a resolved role DISPLAYS as. `Admin` is the API's
 * ADMIN (workspace-write-access.ts) and is a label only — it is not a level
 * anyone can be assigned, so it stays out of WorkspaceAccessLevel.
 */
export type WorkspaceAccessLabel = WorkspaceAccessLevel | 'Admin';

/** Workspace role strings returned by `/workspaces/:id/role` hook (legacy uppercase). */
export const WORKSPACE_HOOK_ROLE = {
  OWNER: 'OWNER',
  ADMIN: 'ADMIN',
  MEMBER: 'MEMBER',
  GUEST: 'GUEST',
} as const;

function isDeliveryOrHookOwner(role: string): boolean {
  return role === 'delivery_owner' || role === WORKSPACE_HOOK_ROLE.OWNER;
}

/**
 * PROMPT 5 Part D: Map internal workspace role to UI access level
 *
 * Mapping:
 * - workspace_owner or delivery_owner or OWNER → Owner
 * - ADMIN → Admin (ONE-TRUTH-FE-1: was Member)
 * - workspace_member or MEMBER → Member
 * - stakeholder or workspace_viewer or GUEST → Guest
 *
 * Supports both snake_case workspace roles and UPPERCASE hook roles
 */
export function mapRoleToAccessLevel(
  role:
    | WorkspaceRole
    | (typeof WORKSPACE_HOOK_ROLE)[keyof typeof WORKSPACE_HOOK_ROLE]
    | null
    | undefined,
): WorkspaceAccessLabel {
  if (!role) return 'Guest';

  if (isWorkspaceOwner(role) || isDeliveryOrHookOwner(role)) {
    return 'Owner';
  }
  if (role === WORKSPACE_HOOK_ROLE.ADMIN) {
    return 'Admin';
  }
  if (isWorkspaceMember(role) || role === WORKSPACE_HOOK_ROLE.MEMBER) {
    return 'Member';
  }
  if (
    isWorkspaceViewer(role) ||
    role === 'stakeholder' ||
    role === WORKSPACE_HOOK_ROLE.GUEST
  ) {
    return 'Guest';
  }
  return 'Guest';
}

/**
 * PROMPT 5 Part D: Map UI access level to internal workspace role
 *
 * Note: This is used when inviting/changing roles. We map:
 * - Owner → workspace_owner
 * - Member → workspace_member
 * - Guest → workspace_viewer
 */
export function mapAccessLevelToRole(accessLevel: WorkspaceAccessLevel): WorkspaceRole {
  switch (accessLevel) {
    case 'Owner':
      return 'workspace_owner';
    case 'Member':
      return 'workspace_member';
    case 'Guest':
      return 'workspace_viewer';
  }
}

/**
 * Get platform role display name
 */
export function getPlatformRoleDisplay(role: string | null | undefined): 'Admin' | 'Member' | 'Guest' {
  const normalized = normalizePlatformRole(role);
  if (normalized === PLATFORM_ROLE.ADMIN) return 'Admin';
  if (normalized === PLATFORM_ROLE.MEMBER) return 'Member';
  return 'Guest';
}

const ORG_ROLE_LABELS: Record<OrgRole, string> = {
  owner: 'Owner',
  admin: 'Admin',
  member: 'Member',
  viewer: 'Viewer',
};

/**
 * ONE-TRUTH-FE-2: a workspace member's org role as the members list shows it,
 * from the backend's `orgRole` (user_organizations.role, #1660). No org row is
 * said as such — never defaulted to a role.
 */
export function memberOrgRoleLabel(orgRole: string | null | undefined): string {
  if (orgRole == null || orgRole === '') return 'No org role';
  return (ORG_ROLES as readonly string[]).includes(orgRole) ? ORG_ROLE_LABELS[orgRole as OrgRole] : 'Unknown role';
}
